import fontkit from '@pdf-lib/fontkit';
import { Buffer } from 'buffer';
import { PDFDocument, rgb } from 'pdf-lib';

/**
 * Fetches a font from a URL and returns a Uint8Array.
 * @param {string} url - The CDN URL of the font.
 * @returns {Promise<Uint8Array>}
 */
export async function fetchFont(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch font from ${url}`);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
}


// --- Constants (Standard Defaults) ---
// These ensure that the standalone utility behaves exactly like the layout engine.
const DEFAULT_FONT_SIZE = 13;
const DEFAULT_CHARACTER_SPACING = 0;
const DEFAULT_LINE_HEIGHT = 1;
const FONT_SIZE_ADJUSTMENT = 0.5;
const DEFAULT_DYNAMIC_FIT = 'vertical';
const DYNAMIC_FIT_HORIZONTAL = 'horizontal';
const DYNAMIC_FIT_VERTICAL = 'vertical';
const VERTICAL_ALIGN_TOP = 'top';
const VERTICAL_ALIGN_MIDDLE = 'middle';
const VERTICAL_ALIGN_BOTTOM = 'bottom';
const DEFAULT_ALIGNMENT = 'left';
const DEFAULT_VERTICAL_ALIGNMENT = 'top';
const ALIGN_JUSTIFY = 'justify';

// --- Unit Conversion ---
// template measurements are in millimeters (mm).
// PDF documents and pdf-lib use points (pt). 1mm = 2.8346 pt.
const mm2pt = (mm) => mm * 2.834645669291339;
const pt2mm = (pt) => pt / 2.834645669291339;

// --- Inlined Layout Helpers (Internal Math borrowed from) ---

const getFontDescentInPt = (fontKitFont, fontSize) => {
    const { descent, unitsPerEm } = fontKitFont;
    return (descent / unitsPerEm) * fontSize;
};

const heightOfFontAtSize = (fontKitFont, fontSize) => {
    const { ascent, descent, bbox, unitsPerEm } = fontKitFont;
    const scale = 1000 / unitsPerEm;
    const yTop = (ascent || bbox.maxY) * scale;
    const yBottom = (descent || bbox.minY) * scale;
    let height = yTop - yBottom;
    height -= Math.abs(descent * scale) || 0;
    return (height / 1000) * fontSize;
};

/**
 * Calculates the exact horizontal width of a text string.
 * Example: If we are highlighting {Prospect_Names} in the string "Hi {Prospect_Names},",
 * we first substitute to get "Hi John & Jane Doe,".
 * We use this function to measure "Hi " so we know exactly where "John & Jane Doe" begins.
 */
const widthOfTextAtSize = (text, fontKitFont, fontSize, characterSpacing, isOffset = false) => {
    if (!text) return 0;
    const { glyphs } = fontKitFont.layout(text);
    const scale = 1000 / fontKitFont.unitsPerEm;
    const standardWidth = glyphs.reduce((totalWidth, glyph) => totalWidth + glyph.advanceWidth * scale, 0) * (fontSize / 1000);
    // When calculating an offset (position where next text starts), we include the spacing after the last character.
    // When calculating the width of a segment, we exclude the spacing after the last character.
    const spacingCount = isOffset ? text.length : Math.max(0, text.length - 1);
    return standardWidth + spacingCount * characterSpacing;
};

const getFontKitFont = (fontBuffer) => {
    const data = fontBuffer instanceof Buffer ? fontBuffer : Buffer.from(fontBuffer);
    return fontkit.create(data);
};

/**
 * Looks up a font by name with a fall-back that ignores spaces/hyphens.
 * Lets the template's "DejaVuSerif Bold" match a fontMap key of "DejaVuSerif-Bold".
 */
const resolveFont = (fontMap, fontName) => {
    if (!fontName) return null;
    if (fontMap[fontName]) return fontMap[fontName];
    const normalize = (s) => String(s).replace(/[\s_-]/g, '').toLowerCase();
    const target = normalize(fontName);
    for (const key of Object.keys(fontMap)) {
        if (normalize(key) === target) return fontMap[key];
    }
    return null;
};

/**
 * Replicates word-wrapping logic.
 * Returns an array of { text, consumed, isParagraphEnd } where:
 *   - text:           the visible content of the line (trailing whitespace stripped, used for measurement & rendering)
 *   - consumed:       number of characters from the ORIGINAL paragraph this line accounts for, including any
 *                     trailing whitespace that was trimmed off the visible portion. Used so highlight ranges,
 *                     computed against the un-wrapped fullText, stay in sync across line boundaries.
 *   - isParagraphEnd: true if this is the final wrapped line of its source paragraph (relevant for justify, where
 *                     the last line of a paragraph renders left-aligned).
 */
const getSplittedLinesBySegmenter = (line, calcValues) => {
    if (line === '' || line.trim() === '') {
        return [{ text: '', consumed: line.length, isParagraphEnd: true }];
    }
    const { font, fontSize, characterSpacing, boxWidthInPt } = calcValues;
    const trimmedInput = line.trimEnd();
    const trailingWS = line.length - trimmedInput.length;

    const segmenter = new Intl.Segmenter('en-US', { granularity: 'word' });
    const iterator = segmenter.segment(trimmedInput)[Symbol.iterator]();

    const rawLines = [];
    let lineCounter = 0;
    let currentTextSize = 0;

    while (true) {
        const chunk = iterator.next();
        if (chunk.done) break;
        const segment = chunk.value.segment;
        const textWidth = widthOfTextAtSize(segment, font, fontSize, characterSpacing, false);
        const offsetWidth = widthOfTextAtSize(segment, font, fontSize, characterSpacing, true);

        if (currentTextSize + textWidth <= boxWidthInPt + 0.1) {
            if (rawLines[lineCounter]) {
                rawLines[lineCounter] += segment;
                currentTextSize += offsetWidth;
            } else {
                rawLines[lineCounter] = segment;
                currentTextSize = offsetWidth;
            }
        } else if (segment.trim() === '') {
            // Pure whitespace that overflowed: keep it attached to the current line so we
            // don't lose those characters from `consumed`, then start a fresh line.
            rawLines[lineCounter] = (rawLines[lineCounter] || '') + segment;
            rawLines[++lineCounter] = '';
            currentTextSize = 0;
        } else if (textWidth <= boxWidthInPt + 0.1) {
            rawLines[++lineCounter] = segment;
            currentTextSize = offsetWidth;
        } else {
            // Single segment is wider than the box: break per character.
            for (const char of segment) {
                const charWidth = widthOfTextAtSize(char, font, fontSize, characterSpacing, false);
                const charOffset = widthOfTextAtSize(char, font, fontSize, characterSpacing, true);
                if (currentTextSize + charWidth <= boxWidthInPt + 0.1) {
                    if (rawLines[lineCounter]) {
                        rawLines[lineCounter] += char;
                        currentTextSize += charOffset;
                    } else {
                        rawLines[lineCounter] = char;
                        currentTextSize = charOffset;
                    }
                } else {
                    rawLines[++lineCounter] = char;
                    currentTextSize = charOffset;
                }
            }
        }
    }

    // Make sure we always have at least one entry even if the loop produced none.
    if (rawLines.length === 0) rawLines.push('');

    const result = rawLines.map((raw, idx) => {
        const text = raw.replace(/\s+$/, '');
        return {
            text,
            consumed: raw.length,
            isParagraphEnd: idx === rawLines.length - 1,
        };
    });

    // Trailing whitespace that was trimmed off the input belongs to the last visible line's `consumed`.
    if (trailingWS > 0) {
        result[result.length - 1].consumed += trailingWS;
    }
    return result;
};

/**
 * Handles 'Dynamic Font Size' scaling.
 * If {Prospect_Names} is very long, the engine shrinks the font to fit. This function
 * re-calculates that exact shrunk font size so the highlight rectangle matches perfectly.
 */
const calculateDynamicFontSize = ({ schema, fontKitFont, value }) => {

    const { fontSize: schemaFontSize, dynamicFontSize: dynamicFontSizeSetting, characterSpacing: schemaCharacterSpacing = DEFAULT_CHARACTER_SPACING, width: boxWidth, height: boxHeight, lineHeight = DEFAULT_LINE_HEIGHT } = schema;
    const fontSize = schemaFontSize || DEFAULT_FONT_SIZE;
    if (!dynamicFontSizeSetting) return fontSize;
    let dynamicFontSize = fontSize;
    const dynamicFontFit = dynamicFontSizeSetting.fit ?? DEFAULT_DYNAMIC_FIT;

    const calculateConstraints = (size) => {

        let totalWidthInMm = 0, totalHeightInMm = 0;
        const boxWidthInPt = mm2pt(boxWidth);
        const firstLineTextHeight = heightOfFontAtSize(fontKitFont, size);
        const firstLineHeightInMm = pt2mm(firstLineTextHeight * lineHeight);
        const otherRowHeightInMm = pt2mm(size * lineHeight);
        value.split('\n').forEach((paragraph, paraIndex) => {
            const lines = getSplittedLinesBySegmenter(paragraph, { font: fontKitFont, fontSize: size, characterSpacing: schemaCharacterSpacing, boxWidthInPt });
            lines.forEach((line, lineIndex) => {
                if (dynamicFontFit === DYNAMIC_FIT_VERTICAL) totalWidthInMm = Math.max(totalWidthInMm, pt2mm(widthOfTextAtSize(line.text, fontKitFont, size, schemaCharacterSpacing, false)));
                if (paraIndex + lineIndex === 0) totalHeightInMm += firstLineHeightInMm;
                else totalHeightInMm += otherRowHeightInMm;
            });
            if (dynamicFontFit === DYNAMIC_FIT_HORIZONTAL) totalWidthInMm = Math.max(totalWidthInMm, pt2mm(widthOfTextAtSize(paragraph, fontKitFont, size, schemaCharacterSpacing, false)));
        });
        return { totalWidthInMm, totalHeightInMm };
    };

    let { totalWidthInMm, totalHeightInMm } = calculateConstraints(dynamicFontSize);
    const maxFontSize = parseFloat(dynamicFontSizeSetting.max) || 100;
    const minFontSize = parseFloat(dynamicFontSizeSetting.min) || 1;

    while (dynamicFontSize < maxFontSize && (dynamicFontFit === DYNAMIC_FIT_HORIZONTAL ? totalWidthInMm < boxWidth : totalHeightInMm < boxHeight)) {
        dynamicFontSize += FONT_SIZE_ADJUSTMENT;
        const { totalWidthInMm: nw, totalHeightInMm: nh } = calculateConstraints(dynamicFontSize);
        if (nh < boxHeight) { totalWidthInMm = nw; totalHeightInMm = nh; } else { dynamicFontSize -= FONT_SIZE_ADJUSTMENT; break; }
    }
    while (dynamicFontSize > minFontSize && (totalWidthInMm > boxWidth || totalHeightInMm > boxHeight)) {
        dynamicFontSize -= FONT_SIZE_ADJUSTMENT;
        ({ totalWidthInMm, totalHeightInMm } = calculateConstraints(dynamicFontSize));
    }

    return dynamicFontSize;
};

/*
 * Splits fullText into wrapped lines that match the layout engine's output,
 * preserving paragraph-boundary metadata and char-consumption counts that the
 * highlight loop needs to stay aligned with highlightRanges.
 */
const splitTextToSize = (value, characterSpacing, fontSize, fontKitFont, boxWidthInPt) => {
    const paragraphs = value.split(/\r\n|\r|\n|\f/g);
    const out = [];
    paragraphs.forEach((para, pIdx) => {
        const paraLines = getSplittedLinesBySegmenter(para, { font: fontKitFont, fontSize, characterSpacing, boxWidthInPt });
        // The paragraph-separating "\n" itself is a character in fullText. Charge it to the
        // last wrapped line of this paragraph so currentOffset jumps over it before the next line.
        if (pIdx < paragraphs.length - 1 && paraLines.length > 0) {
            paraLines[paraLines.length - 1].consumed += 1;
        }
        out.push(...paraLines);
    });
    return out;
};

// --- Variables & Positioning ---

/**
 * Merges template text with variable values and marks where the highlights should go.
 * Example for Postcard:
 * Template: "To discuss... {Target_Area} home"
 * variables: { Target_Area: "Downtown Area" }
 * Result: "To discuss... Downtown Area home"
 * Ranges: Marks that characters 28 to 41 ("Downtown Area") should be highlighted.
 */
const substituteWithPositions = (templateText, variables, highlightVariables) => {
    const highlightVars = Array.isArray(highlightVariables) ? highlightVariables : [highlightVariables];
    const ranges = [];
    const parts = templateText.split(/({[^{}]+})/);
    let currentText = '';
    parts.forEach(part => {
        if (part.startsWith('{') && part.endsWith('}')) {
            const varName = part.slice(1, -1);

            const value = String(variables[varName] ?? '');
            // If this is a variable we want to highlight (e.g., Prospect_Names),
            // we save its exact start and end position in the final full string.
            if (highlightVars.includes(varName)) {
                ranges.push({ start: currentText.length, end: currentText.length + value.length });
            }
            currentText += value;
        } else {
            currentText += part;
        }
    });
    return { text: currentText, ranges };
};

const rotatePoint = (point, pivot, angle) => {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    return { x: cos * (point.x - pivot.x) - sin * (point.y - pivot.y) + pivot.x, y: sin * (point.x - pivot.x) + cos * (point.y - pivot.y) + pivot.y };
};

// --- Main Standalone Utility ---

/**
 * Core function to apply highlights to an existing PDF.
 *
 * @param {Uint8Array|ArrayBuffer} pdfBinaryData - The existing PDF document binary.
 * @param {Object} template - The Postcard template JSON.
 * @param {Object} input - Data record (e.g., { Prospect_Names: "Vivek", ... }).
 * @param {string|string[]} highlightVariables - Variable name (or array of names) to highlight.
 * @param {Object} fontMap - Mapping of 'Font Name' to Buffer data.
 * @returns {Promise<Uint8Array>} - The highlighted PDF binary.
 */
export async function applyHighlights(pdfBinaryData, template, input, highlightVariables, fontMap = {}) {

    const pdfDoc = await PDFDocument.load(pdfBinaryData);
    pdfDoc.registerFontkit(fontkit);
    const cache = new Map();
    const processedRects = new Set(); // To prevent overlapping highlights for duplicate definitions
    const pageCount = pdfDoc.getPageCount();

    const highlightVarsList = Array.isArray(highlightVariables) ? highlightVariables : [highlightVariables];
    const highlightVarsSet = new Set(highlightVarsList);

    // Determine source of fields (Flat Schema Array or Contact/Customer Structure)
    let fields = [];
    if (Array.isArray(template)) {

        // If template is a flat array of schema objects, wrap them in a pseudo-config structure
        fields = template.map(item => ({
            ...item,
            config: item.config || [{ ...item, page: item.page || 1 }]
        }));
    } else {
        fields = [...(template.contact || []), ...(template.customer || [])];
    }

    for (const field of fields) {


        if (!field.config || !Array.isArray(field.config)) continue;

        for (const configEntry of field.config) {
            const pageIndex = parseInt(configEntry.page) - 1;
            if (isNaN(pageIndex) || pageIndex < 0 || pageIndex >= pageCount) continue;

            const page = pdfDoc.getPage(pageIndex);
            const pageHeight = page.getHeight();

            // Build a virtual schema by combining field and configEntry metadata
            const schema = {
                name: field.variableName || field.name,
                type: configEntry.text || field.text ? 'multiVariableText' : (field.type || configEntry.type || 'text'),
                content: field.defaultValue || '',
                text: configEntry.text || field.text || '',
                fontName: configEntry.fontName || field.fontName || 'Helvetica',
                fontSize: parseFloat(configEntry.fontSize || field.fontSize) || DEFAULT_FONT_SIZE,
                characterSpacing: parseFloat(configEntry.characterSpacing || field.characterSpacing) || DEFAULT_CHARACTER_SPACING,
                lineHeight: parseFloat(configEntry.lineHeight || field.lineHeight) || DEFAULT_LINE_HEIGHT,
                alignment: configEntry.alignment || field.alignment || DEFAULT_ALIGNMENT,
                verticalAlignment: configEntry.verticalAlignment || field.verticalAlignment || DEFAULT_VERTICAL_ALIGNMENT,
                rotate: parseFloat(configEntry.rotate || field.rotate) || 0,
                width: parseFloat(configEntry.width || field.width) || 0,
                height: parseFloat(configEntry.height || field.height) || 0,
                dynamicFontSize: configEntry.dynamicFontSize || field.dynamicFontSize,
                position: {
                    x: parseFloat(configEntry.x || (field.position && field.position.x)) || 0,
                    y: parseFloat(configEntry.y || (field.position && field.position.y)) || 0
                }
            };


            // Optimization: Only process fields that actually match a requested highlight variable
            const isTextType = schema.type === 'text' || schema.type === 'multiVariableText';
            const configType = (configEntry.type || field.type || '').toLowerCase();
            const isVisualField = schema.type === 'qrcode' || schema.type === 'image' || schema.type === 'file' ||
                configType === 'qrcode' || configType === 'image';

            const isTarget = highlightVarsSet.has(schema.name) ||
                (isTextType && highlightVarsList.some(hv => schema.text.includes(`{${hv}}`)));
            if (!isTarget) continue;

            // Skip anything we don't know how to highlight (e.g. raw SVG, etc.)
            if (!isTextType && !isVisualField) continue;

            // --- Non-text elements: QR codes and images ---
            // These have a fixed bounding box, so we just draw a coloured border around it.
            if (isVisualField) {
                const vx = mm2pt(schema.position.x);
                const vw = mm2pt(schema.width);
                const vh = mm2pt(schema.height);
                // PDF coordinate origin is bottom-left, template origin is top-left.
                const vy = pageHeight - mm2pt(schema.position.y) - vh;
                const vRotate = schema.rotate ? { angle: schema.rotate, type: 'degrees' } : { angle: 0, type: 'degrees' };

                const rectKey = `${pageIndex}-V-${vx.toFixed(2)}-${vy.toFixed(2)}-${vw.toFixed(2)}-${vh.toFixed(2)}`;
                if (processedRects.has(rectKey)) continue;
                processedRects.add(rectKey);

                page.drawRectangle({
                    x: vx, y: vy, width: vw, height: vh,
                    color: rgb(1, 1, 0),       // yellow tint fill
                    opacity: 0.18,
                    borderColor: rgb(1, 0.65, 0), // amber border
                    borderWidth: 2.5,
                    borderOpacity: 1,
                    rotate: vRotate,
                });
                continue;
            }

            // --- Text Precision Highlighting Logic ---
            const fontName = schema.fontName;
            const fontData = resolveFont(fontMap, fontName);
            if (!fontData) {
                console.warn(`Font "${fontName}" not found in fontMap. Skipping highlight for field "${schema.name}".`);
                continue;
            }

            if (!cache.has(fontName)) {
                cache.set(fontName, getFontKitFont(fontData.data || fontData));
            }
            const fontKitFont = cache.get(fontName);

            let fullText = '', highlightRanges = [];
            if (schema.type === 'multiVariableText') {
                const result = substituteWithPositions(schema.text, input || {}, highlightVarsList);
                fullText = result.text;
                highlightRanges = result.ranges;
            } else if (schema.type === 'text') {
                fullText = String((input?.[schema.name]) ?? schema.content ?? '');
                if (highlightVarsSet.has(schema.name)) highlightRanges = [{ start: 0, end: fullText.length }];
            }

            if (highlightRanges.length === 0) continue;

            // Extract layout properties from the template.
            const fontSize = schema.dynamicFontSize ? calculateDynamicFontSize({ schema, fontKitFont, value: fullText }) : (schema.fontSize ?? DEFAULT_FONT_SIZE);

            const lineHeight = schema.lineHeight ?? DEFAULT_LINE_HEIGHT, characterSpacing = schema.characterSpacing ?? DEFAULT_CHARACTER_SPACING;

            const alignment = schema.alignment ?? DEFAULT_ALIGNMENT, verticalAlignment = schema.verticalAlignment ?? DEFAULT_VERTICAL_ALIGNMENT;

            const width = mm2pt(schema.width), height = mm2pt(schema.height);

            const rotate = schema.rotate ? { angle: schema.rotate, type: 'degrees' } : { angle: 0, type: 'degrees' };

            const x = mm2pt(schema.position.x);

            // Pivot point is used if the text block is rotated (like the 'Above Asking Price' ribbon).
            const pivotPoint = { x: x + width / 2, y: pageHeight - mm2pt(schema.position.y) - height / 2 };
            /**
             * LINE BREAKS:
             * This breaks the final text into lines.
             * If {Team_Name} and {Callback_Number} wrap onto multiple lines,
             * 'lines' will contain each fragment separately as { text, consumed, isParagraphEnd }.
             */
            const lines = splitTextToSize(fullText, characterSpacing, fontSize, fontKitFont, width);
            const firstLineHeight = heightOfFontAtSize(fontKitFont, fontSize), fontDescent = getFontDescentInPt(fontKitFont, fontSize);
            const halfLineAdj = lineHeight === 0 ? 0 : ((lineHeight - 1) * fontSize) / 2;
            // Calculate exact vertical offset based on 'top', 'middle', or 'bottom' alignment.
            let yOffset = verticalAlignment === VERTICAL_ALIGN_TOP ? firstLineHeight + halfLineAdj :
                (verticalAlignment === VERTICAL_ALIGN_BOTTOM ? height - (lineHeight * fontSize * (lines.length - 1)) + fontDescent - halfLineAdj :
                    (height - (lineHeight * fontSize * (lines.length - 1)) - firstLineHeight + fontDescent) / 2 + firstLineHeight);
            let currentOffset = 0; // Tracks character count across lines, kept in sync with fullText indices.
            lines.forEach((line, rowIndex) => {
                const trimmedLine = line.text;
                const lineStart = currentOffset;
                const lineEnd = currentOffset + trimmedLine.length;

                // JUSTIFY: for non-final lines of a justified paragraph, the renderer distributes
                // extra space across word gaps so the line fills the full box width. We recreate
                // that per-space slack so highlight offsets within the line are accurate.
                const tWidth = widthOfTextAtSize(trimmedLine, fontKitFont, fontSize, characterSpacing, false);
                let justifyExtraSpace = 0;
                const justifyApplies = alignment === ALIGN_JUSTIFY && !line.isParagraphEnd && trimmedLine.length > 0;
                if (justifyApplies) {
                    const spaceMatches = trimmedLine.match(/ /g);
                    const spaceCount = spaceMatches ? spaceMatches.length : 0;
                    if (spaceCount > 0 && width > tWidth) {
                        justifyExtraSpace = (width - tWidth) / spaceCount;
                    }
                }

                // Horizontal start position for this line based on alignment.
                let xLine;
                if (alignment === 'center') xLine = x + (width - tWidth) / 2;
                else if (alignment === 'right') xLine = x + (width - tWidth);
                else xLine = x; // left or justify

                // For each highlighting target, check if any part of it is on this line.
                highlightRanges.forEach(range => {
                    // Find intersection between variable range and current line range.
                    const start = Math.max(range.start, lineStart), end = Math.min(range.end, lineEnd);
                    if (start < end) {
                        /**
                         * PRECISION PLACEMENT:
                         * 1. Measurement: Width of text BEFORE the variable on this line (prefix).
                         * 2. Measurement: Width of the variable part on this line (hlText).
                         */
                        const prefix = trimmedLine.substring(0, start - lineStart);
                        const hlText = trimmedLine.substring(start - lineStart, end - lineStart);

                        let xOffset = widthOfTextAtSize(prefix, fontKitFont, fontSize, characterSpacing, true);
                        let hlWidth = widthOfTextAtSize(hlText, fontKitFont, fontSize, characterSpacing, false);

                        if (justifyExtraSpace > 0) {
                            const prefixSpaces = (prefix.match(/ /g) || []).length;
                            const hlSpaces = (hlText.match(/ /g) || []).length;
                            xOffset += prefixSpaces * justifyExtraSpace;
                            hlWidth += hlSpaces * justifyExtraSpace;
                        }

                        // Vertical coordinate in PDF space (bottom-left origin).
                        const yLine = pageHeight - mm2pt(schema.position.y) - yOffset - (lineHeight * fontSize * rowIndex);
                        // Accurate height math using font metrics.
                        const { ascent, descent, unitsPerEm } = fontKitFont;
                        const fontHeight = ((ascent - descent) / unitsPerEm) * fontSize, currentFontDescent = (descent / unitsPerEm) * fontSize;

                        const hX = xLine + xOffset, hY = yLine + currentFontDescent, hH = fontHeight;
                        // Create a unique key for this rectangle to de-duplicate highlights
                        const rectKey = `${pageIndex}-${hX.toFixed(2)}-${hY.toFixed(2)}-${hlWidth.toFixed(2)}-${hH.toFixed(2)}`;
                        if (processedRects.has(rectKey)) {
                            return;
                        }
                        processedRects.add(rectKey);
                        const rectProps = { x: hX, y: hY, width: hlWidth, height: hH, color: rgb(1, 1, 0), opacity: 0.5, rotate };
                        // Apply rotation transform if needed.
                        if (rotate.angle !== 0) {
                            const rp = rotatePoint({ x: hX, y: hY }, pivotPoint, rotate.angle); rectProps.x = rp.x; rectProps.y = rp.y;
                        }
                        // DRAW RECTANGLE!
                        page.drawRectangle(rectProps);
                    }
                });

                // Advance the global pointer by the ORIGINAL chars this wrapped line accounts for
                // (visible text + any trimmed trailing whitespace + any paragraph-separator \n).
                currentOffset += line.consumed;
            });
        }
    }
    return await pdfDoc.save();
}
